/**
 * usePushNotifications — minimal hook around the Web Push API.
 *
 * Responsibilities (matches the FastAPI side at /api/push/*):
 *   1. fetch the VAPID public key once
 *   2. expose `permission`, `isSupported`, `isSubscribed`
 *   3. `enable()`     → asks for permission → subscribes → POSTs to backend
 *   4. `disable()`    → unsubscribes locally + tells backend to delete
 *   5. `sendTest()`   → POST /api/push/test (round-trip sanity check)
 *
 * iOS Safari note: web-push only works when the PWA is installed to the
 * home screen on iOS 16.4+. The hook surfaces `iosNeedsInstall` so the
 * UI can show "Add to Home Screen" instructions when relevant.
 */
import { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config/api';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

function detectIosNeedsInstall() {
  const ua = navigator.userAgent || '';
  const isIos = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  if (!isIos) return false;
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  return !standalone; // on iOS, push requires Home-Screen install
}

export function usePushNotifications() {
  const supported =
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window;
  const [permission, setPermission] = useState(
    supported ? Notification.permission : 'denied'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [iosNeedsInstall, setIosNeedsInstall] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setIosNeedsInstall(detectIosNeedsInstall());
    if (!supported) return;
    navigator.serviceWorker.ready
      .then((reg) => reg.pushManager.getSubscription())
      .then((sub) => setIsSubscribed(!!sub))
      .catch(() => setIsSubscribed(false));
  }, [supported]);

  const enable = useCallback(async () => {
    if (!supported) throw new Error('Push not supported in this browser.');
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') throw new Error('Notification permission denied.');

      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        const keyRes = await fetch(`${API_URL}/api/push/vapid-key`, {
          credentials: 'include',
        });
        if (!keyRes.ok) throw new Error(`vapid-key HTTP ${keyRes.status}`);
        const { public_key: pubKey } = await keyRes.json();
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(pubKey),
        });
      }
      const subJson = sub.toJSON();
      const res = await fetch(`${API_URL}/api/push/subscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          endpoint: subJson.endpoint,
          keys: subJson.keys,
          expiration_time: subJson.expirationTime || null,
          ua: navigator.userAgent,
        }),
      });
      if (!res.ok) throw new Error(`subscribe HTTP ${res.status}`);
      setIsSubscribed(true);
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const disable = useCallback(async () => {
    if (!supported) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch(
          `${API_URL}/api/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`,
          { method: 'DELETE', credentials: 'include' }
        ).catch(() => {});
        await sub.unsubscribe();
      }
      setIsSubscribed(false);
    } finally {
      setBusy(false);
    }
  }, [supported]);

  const sendTest = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/push/test`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!res.ok) throw new Error(`test HTTP ${res.status}`);
    return res.json();
  }, []);

  return {
    supported,
    permission,
    isSubscribed,
    iosNeedsInstall,
    busy,
    enable,
    disable,
    sendTest,
  };
}
